// src/pages/societybook/BillPrint/BillPrintView.jsx
// v2 - Clean professional design, society data from DB, print all bills support
import React, { useRef, useEffect, useState } from 'react';
import { getSociety } from '../../../api/society.api';
import { getTerms } from '../../../api/societybook.api';

const fmt = (n) =>
  Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

const numberToWords = (num) => {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight',
    'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen',
    'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const convert = (n) => {
    if (n === 0) return '';
    if (n < 20) return ones[n] + ' ';
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '') + ' ';
    if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred ' + convert(n % 100);
    if (n < 100000) return convert(Math.floor(n / 1000)) + 'Thousand ' + convert(n % 1000);
    if (n < 10000000) return convert(Math.floor(n / 100000)) + 'Lakh ' + convert(n % 100000);
    return convert(Math.floor(n / 10000000)) + 'Crore ' + convert(n % 10000000);
  };
  const intPart = Math.floor(num);
  const decPart = Math.round((num - intPart) * 100);
  let result = 'INR ' + convert(intPart).trim();
  if (decPart > 0) result += ` and ${convert(decPart).trim()} Paise`;
  return result + ' Only';
};

// ── Single bill HTML generator ───────────────────────────────────────────────
const buildBillHTML = (bill, society, terms) => {
  const addr = society?.address
    ? [society.address.line1, society.address.city, society.address.state, society.address.pincode]
        .filter(Boolean).join(', ')
    : '';

  const lines = (bill.billLines || []).map((line, i) => `
    <tr>
      <td style="padding:7px 10px;border-bottom:1px solid #eee;text-align:center">${i + 1}</td>
      <td style="padding:7px 10px;border-bottom:1px solid #eee">${line.chargeHeadName}</td>
      <td style="padding:7px 10px;border-bottom:1px solid #eee;text-align:right">${fmt(line.amount)}</td>
    </tr>`).join('');

  const extraRows = [
    bill.interestOnDelay > 0 ? `<tr><td colspan="2" style="padding:6px 10px;text-align:right;color:#555">Interest on Delayed Payment</td><td style="padding:6px 10px;text-align:right">${fmt(bill.interestOnDelay)}</td></tr>` : '',
    bill.cgst > 0 ? `<tr><td colspan="2" style="padding:6px 10px;text-align:right;color:#555">CGST</td><td style="padding:6px 10px;text-align:right">${fmt(bill.cgst)}</td></tr>` : '',
    bill.sgst > 0 ? `<tr><td colspan="2" style="padding:6px 10px;text-align:right;color:#555">SGST</td><td style="padding:6px 10px;text-align:right">${fmt(bill.sgst)}</td></tr>` : '',
    bill.previousBalance > 0 ? `<tr><td colspan="2" style="padding:6px 10px;text-align:right;color:#555">Previous Balance Carried Forward</td><td style="padding:6px 10px;text-align:right">${fmt(bill.previousBalance)} ${bill.previousBalanceType}</td></tr>` : '',
  ].filter(Boolean).join('');

  return `
    <div style="width:700px;margin:0 auto;font-family:Arial,sans-serif;font-size:12px;color:#111;border:1px solid #ccc;page-break-after:always">

      <!-- Header -->
      <div style="text-align:center;padding:18px 20px 12px;border-bottom:2px solid #111">
        <div style="font-size:18px;font-weight:700;letter-spacing:0.5px;margin-bottom:4px">${society?.name || 'Society'}</div>
        ${addr ? `<div style="font-size:11px;color:#444;margin-bottom:3px">${addr}</div>` : ''}
        ${society?.registrationNumber ? `<div style="font-size:11px;color:#444">Reg. No: ${society.registrationNumber}</div>` : ''}
      </div>

      <!-- Title bar -->
      <div style="text-align:center;padding:7px;background:#111;color:#fff;font-size:12px;font-weight:700;letter-spacing:3px">
        MAINTENANCE BILL
      </div>

      <!-- Meta -->
      <div style="display:flex;justify-content:space-between;padding:12px 16px;border-bottom:1px solid #ddd">
        <div>
          <div style="margin-bottom:5px"><span style="color:#555;font-size:11px">Member: </span><strong>${bill.flatNo} — ${bill.memberName}</strong></div>
          <div><span style="color:#555;font-size:11px">Period: </span><strong>${fmtDate(bill.periodFrom)} to ${fmtDate(bill.periodTo)}</strong></div>
        </div>
        <div style="text-align:right">
          <div style="margin-bottom:5px"><span style="color:#555;font-size:11px">Bill No: </span><strong>#${bill.billNo}</strong></div>
          <div style="margin-bottom:5px"><span style="color:#555;font-size:11px">Date: </span><strong>${fmtDate(bill.billDate)}</strong></div>
          <div><span style="color:#555;font-size:11px">Due Date: </span><strong>${fmtDate(bill.dueDate)}</strong></div>
        </div>
      </div>

      <!-- Charges table -->
      <table style="width:100%;border-collapse:collapse">
        <thead>
          <tr style="background:#f5f5f5">
            <th style="padding:8px 10px;text-align:center;width:40px;border-bottom:2px solid #ddd;font-size:11px">Sr.</th>
            <th style="padding:8px 10px;text-align:left;border-bottom:2px solid #ddd;font-size:11px">Nature of Charges</th>
            <th style="padding:8px 10px;text-align:right;width:120px;border-bottom:2px solid #ddd;font-size:11px">Amount (₹)</th>
          </tr>
        </thead>
        <tbody>
          ${lines}
          <!-- Sub total -->
          <tr style="background:#f9f9f9">
            <td colspan="2" style="padding:7px 10px;text-align:right;font-weight:600;border-top:1px solid #ddd">Sub Total</td>
            <td style="padding:7px 10px;text-align:right;font-weight:600;border-top:1px solid #ddd">${fmt(bill.subTotal)}</td>
          </tr>
          ${extraRows}
          <!-- Total -->
          <tr style="background:#111;color:#fff">
            <td colspan="2" style="padding:9px 10px;text-align:right;font-weight:700;font-size:13px">Total</td>
            <td style="padding:9px 10px;text-align:right;font-weight:700;font-size:13px">${fmt(bill.totalDue)}</td>
          </tr>
          ${bill.adjustedAdvance > 0 ? `
          <tr>
            <td colspan="2" style="padding:7px 10px;text-align:right;color:#555">Less: Adjusted Advance</td>
            <td style="padding:7px 10px;text-align:right">${fmt(bill.adjustedAdvance)}</td>
          </tr>
          <tr style="background:#f9f9f9">
            <td colspan="2" style="padding:7px 10px;text-align:right;font-weight:700">Net Payable</td>
            <td style="padding:7px 10px;text-align:right;font-weight:700">${fmt(bill.netPayable || bill.totalDue)}</td>
          </tr>` : ''}
        </tbody>
      </table>

      <!-- Amount in words -->
      <div style="padding:10px 16px;border-top:1px solid #ddd;border-bottom:1px solid #ddd;font-style:italic;font-size:11.5px">
        <strong>Amount in Words:</strong> ${numberToWords(bill.totalDue)}
      </div>

      <!-- Terms & Conditions -->
      ${terms ? `
      <div style="padding:10px 16px;border-top:1px solid #ddd;font-size:11px;color:#444">
        <div style="font-weight:700;margin-bottom:6px">Terms & Conditions:</div>
        ${terms.split('\n').filter(l => l.trim()).map((line, i) => `<div style="margin-bottom:3px">${line}</div>`).join('')}
      </div>` : ''}

      <!-- Footer -->
      <div style="display:flex;justify-content:space-between;padding:12px 16px">
        <div style="font-size:11px;color:#444;max-width:380px">
          <div style="font-weight:600;margin-bottom:4px">Payment Instructions:</div>
          <div>Please pay before the due date to avoid interest charges.</div>
        </div>
        <div style="text-align:right;font-size:11px">
          <div>For ${society?.name || 'Society'}</div>
          <div style="margin-top:36px;border-top:1px solid #999;padding-top:4px">Authorised Signatory</div>
        </div>
      </div>

    </div>
  `;
};

// ── Component ────────────────────────────────────────────────────────────────
// Props:
//   bill      — single bill object (for single print)
//   bills     — array of bill objects (for print all)
//   onClose   — close handler
export default function BillPrintView({ bill, bills, onClose }) {
  const [society, setSociety] = useState(null);
  const [terms,   setTerms]   = useState('');
  const societyId = bill?.societyId || bills?.[0]?.societyId;

  useEffect(() => {
    if (societyId) {
      getSociety(societyId).then(r => setSociety(r.data.society)).catch(() => {});
      getTerms(societyId).then(r => setTerms(r.data.data || '')).catch(() => {});
    }
  }, [societyId]);

  const handlePrint = (billsToPrint) => {
    if (!society) {
      setTimeout(() => handlePrint(billsToPrint), 300);
      return;
    }
    const htmlParts = billsToPrint.map(b => buildBillHTML(b, society, terms)).join('<div style="height:20px"></div>');
    const win = window.open('', '_blank', 'width=820,height=1100');
    win.document.write(`
      <html>
        <head>
          <title>Maintenance Bills</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { background: #fff; padding: 20px; }
            @media print {
              body { padding: 0; }
              div[style*="page-break-after"] { page-break-after: always; }
            }
          </style>
        </head>
        <body>${htmlParts}</body>
      </html>
    `);
    win.document.close();
    setTimeout(() => win.print(), 500);
  };

  // Single bill mode
  if (bill) {
    return (
      <div style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
        zIndex: 9999, display: 'flex', flexDirection: 'column',
      }}>
        {/* Toolbar */}
        <div style={{
          background: '#1a1a1a', color: '#fff', padding: '10px 20px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0,
        }}>
          <span style={{ fontSize: '0.9rem' }}>Bill #{bill.billNo} — {bill.memberName} ({bill.flatNo})</span>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={() => handlePrint([bill])}
              style={{ background: '#22c55e', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 16px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>
              🖨 Print / Save PDF
            </button>
            <button
              onClick={onClose}
              style={{ background: '#444', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 14px', cursor: 'pointer', fontSize: '0.85rem' }}>
              ✕ Close
            </button>
          </div>
        </div>

        {/* Preview */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px', background: '#e5e5e5' }}>
          <div
            dangerouslySetInnerHTML={{ __html: buildBillHTML(bill, society, terms) }}
          />
        </div>
      </div>
    );
  }

  // Print all mode
  if (bills) {
    handlePrint(bills);
    onClose();
    return null;
  }

  return null;
}
